import { useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, ShieldAlert, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const ACTION_OPTS = [
    { value: "__all", label: "Todas" },
    { value: "login", label: "login" },
    { value: "create", label: "create" },
    { value: "update", label: "update" },
    { value: "tape.commit", label: "tape.commit" },
];

export default function Bitacora() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [verify, setVerify] = useState(null);
    const [filters, setFilters] = useState({ user_email: "", action: "__all", entity_type: "" });

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.user_email) params.user_email = filters.user_email;
            if (filters.action && filters.action !== "__all") params.action = filters.action;
            if (filters.entity_type) params.entity_type = filters.entity_type;
            params.limit = 500;
            const { data } = await api.get("/bitacora", { params });
            setRows(data);
        } catch (err) {
            toast.error("Error cargando bitácora", {
                description: err?.response?.data?.detail || "Sin acceso",
            });
        } finally {
            setLoading(false);
        }
    };

    const runVerify = async () => {
        try {
            const { data } = await api.get("/bitacora/verify");
            setVerify(data);
            if (data.ok) {
                toast.success(`Cadena íntegra · ${data.total} registros verificados`);
            } else {
                toast.error("Cadena rota", {
                    description: `Break en seq=${data.broken_at}`,
                });
            }
        } catch (err) {
            toast.error("No se pudo verificar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    useEffect(() => {
        load();
        runVerify();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shortHash = (h) => (h ? `${h.slice(0, 10)}…${h.slice(-6)}` : "");

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1600px]" data-testid="bitacora-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Sistema
                    </div>
                    <h1 className="font-display text-4xl tracking-tight">Bitácora inmutable</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Registro append-only con hash SHA-256 encadenado. Cada acción de negocio
                        queda auditada contra el registro anterior.
                    </p>
                </div>
                <div className="kpi-card min-w-[300px]" data-testid="verify-panel">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                        {verify?.ok ? (
                            <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--brand-exito)]" />
                        ) : (
                            <ShieldAlert className="h-3.5 w-3.5 text-[color:var(--brand-coral)]" />
                        )}
                        Integridad
                    </div>
                    <div className="kpi-value mt-2 text-2xl">
                        {verify ? (verify.ok ? "OK" : "ROTA") : "—"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {verify?.ok
                            ? `${verify.total} registros encadenados desde GENESIS`
                            : verify?.broken_at
                              ? `Break en seq=${verify.broken_at}`
                              : "Sin ejecutar"}
                    </div>
                    <Button
                        onClick={runVerify}
                        size="sm"
                        variant="outline"
                        className="mt-3 w-full"
                        data-testid="verify-chain-button"
                    >
                        <RefreshCw className="h-3.5 w-3.5 mr-2" /> Verificar cadena
                    </Button>
                </div>
            </div>

            <div className="kpi-card">
                <div className="flex items-center gap-3 mb-4">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros</span>
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Usuario
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input
                                className="pl-9 mt-1"
                                placeholder="email@…"
                                value={filters.user_email}
                                onChange={(e) =>
                                    setFilters({ ...filters, user_email: e.target.value })
                                }
                                data-testid="filter-user-email"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Acción
                        </label>
                        <Select
                            value={filters.action}
                            onValueChange={(v) => setFilters({ ...filters, action: v })}
                        >
                            <SelectTrigger className="mt-1" data-testid="filter-action">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ACTION_OPTS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Entidad
                        </label>
                        <Input
                            className="mt-1"
                            placeholder="user, tape_corte, session…"
                            value={filters.entity_type}
                            onChange={(e) =>
                                setFilters({ ...filters, entity_type: e.target.value })
                            }
                            data-testid="filter-entity"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={load}
                            className="w-full"
                            disabled={loading}
                            data-testid="filter-apply"
                        >
                            {loading ? "Cargando…" : "Aplicar"}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <div className="overflow-x-auto">
                    <table className="trf-table" data-testid="bitacora-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Timestamp (UTC)</th>
                                <th>Usuario</th>
                                <th>Acción</th>
                                <th>Entidad</th>
                                <th>Prev hash</th>
                                <th>Current hash</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`bitacora-row-${r.seq}`}>
                                    <td className="tabular font-medium">{r.seq}</td>
                                    <td className="tabular text-xs">{r.timestamp}</td>
                                    <td>
                                        <div className="text-sm">{r.user_email}</div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {r.user_id}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge-gold">{r.action}</span>
                                    </td>
                                    <td>
                                        <div className="text-sm">{r.entity_type}</div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {r.entity_id}
                                        </div>
                                    </td>
                                    <td className="hash-cell" title={r.prev_hash}>
                                        {shortHash(r.prev_hash)}
                                    </td>
                                    <td className="hash-cell" title={r.current_hash}>
                                        {shortHash(r.current_hash)}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={7} className="text-center py-10 text-muted-foreground">
                                        Sin registros que coincidan con los filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
